// src/components/dashboard/PostCourseStaffReviewPanel.jsx
import React, { useMemo, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import * as EnrollmentAPI from "../../util/enrollment_api_util";
import "./postcourse_review.css";

// Helpers
const getCourseKeyFromEnrollment = (enr) => {
  const c = enr?.course || {};
  return c.key || c.courseKey || enr?.courseKey || enr?.key || "";
};

const formatDateTimeLocal = (d) => {
  if (!d) return "";
  const dateObj = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleString("en-US");
};

/**
 * ✅ Matches backend schema:
 * enrollment.postCourseProgress = [
 *   { sectionId, status, submission:{text, media[], submittedAt}, review:{status, feedback, reviewedAt} }
 * ]
 */
const getPostCourseSectionRecord = (enrollment, sectionId) => {
  const arr = Array.isArray(enrollment?.postCourseProgress) ? enrollment.postCourseProgress : [];
  const sid = String(sectionId || "");
  const entry = arr.find((e) => String(e?.sectionId || "") === sid) || null;

  if (!entry) return null;

  return {
    sectionId: sid,
    status: String(entry?.status || "").toLowerCase() || "in_progress",

    submittedAt: entry?.submission?.submittedAt || null,
    text: String(entry?.submission?.text || ""),
    media: Array.isArray(entry?.submission?.media) ? entry.submission.media : [],

    reviewStatus: String(entry?.review?.status || "").toLowerCase() || "",
    feedback: String(entry?.review?.feedback || ""),
    reviewMedia: Array.isArray(entry?.review?.media) ? entry.review.media : [],
    reviewedAt: entry?.review?.reviewedAt || null,
  };
};

const isImageFile = (m) => String(m?.fileType || "").startsWith("image/");
const isVideoFile = (m) => String(m?.fileType || "").startsWith("video/");

const autoGrowTextarea = (el) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

function Lightbox({ open, items, startIndex = 0, onClose }) {
  const [idx, setIdx] = useState(startIndex);
  const [zoom, setZoom] = useState(1);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.5;

  useEffect(() => {
    if (!open) return;
    setIdx(startIndex);
    setZoom(1);
  }, [open, startIndex]);

  useEffect(() => {
    if (!open) return;

    // ✅ force top-of-page and prevent background scroll
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch (e) {
      window.scrollTo(0, 0);
    }

    document.documentElement.classList.add("pc-noscroll");
    document.body.classList.add("pc-noscroll");

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") setIdx((v) => (v - 1 + items.length) % items.length);
      if (e.key === "ArrowRight") setIdx((v) => (v + 1) % items.length);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.documentElement.classList.remove("pc-noscroll");
      document.body.classList.remove("pc-noscroll");
    };
  }, [open, items.length, onClose]);

  if (!open) return null;
  if (!Array.isArray(items) || !items.length) return null;

  const cur = items[idx] || {};
  const kind = cur.kind; // "image" | "video"
  const url = cur.url;
  const zoomPct = Math.round(zoom * 100);

  // ✅ portal to body so it always overlays everything
  return createPortal(
    <div className="pc-lightbox" onClick={onClose} role="dialog" aria-modal="true">
      <div className="pc-lightbox-card" onClick={(e) => e.stopPropagation()}>
        <div className="pc-lightbox-actions">
          {kind === "image" && (
            <button
              type="button"
              className="pc-lightbox-actionbtn"
              onClick={() => {
                if (!url) return;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
              aria-label="Open image in full screen"
              title="Open full screen"
            >
              Full screen
            </button>
          )}
          <button type="button" className="pc-lightbox-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="pc-lightbox-stage">
          {kind === "video" ? (
            <video src={url} controls style={{ maxWidth: "100%", maxHeight: "78vh", borderRadius: 14 }} />
          ) : (
            <div className="pc-lightbox-media" aria-label="Zoomable image">
              <img
                src={url}
                alt=""
                className="pc-lightbox-img"
                draggable={false}
                loading="eager"
                decoding="async"
                style={{ width: `${zoomPct}%` }}
                onDoubleClick={() => {
                  if (!url) return;
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              />
            </div>
          )}
        </div>

        {kind === "image" && (
          <div className="pc-lightbox-zoom">
            <button
              type="button"
              className="pc-lightbox-zoombtn"
              onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
              disabled={zoom <= ZOOM_MIN}
              aria-label="Zoom out"
              title="Zoom out"
            >
              −
            </button>
            <button
              type="button"
              className="pc-lightbox-zoombtn"
              onClick={() => setZoom(1)}
              disabled={zoom === 1}
              aria-label="Reset zoom"
              title="Reset zoom"
            >
              {zoomPct}%
            </button>
            <button
              type="button"
              className="pc-lightbox-zoombtn"
              onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
              disabled={zoom >= ZOOM_MAX}
              aria-label="Zoom in"
              title="Zoom in"
            >
              +
            </button>
          </div>
        )}

        {items.length > 1 && (
          <div className="pc-lightbox-nav">
            <button
              type="button"
              className="pc-lightbox-navbtn"
              onClick={() => setIdx((v) => (v - 1 + items.length) % items.length)}
              aria-label="Previous"
              title="Previous"
            >
              ‹
            </button>

            <div className="pc-lightbox-counter">
              {idx + 1} / {items.length}
            </div>

            <button
              type="button"
              className="pc-lightbox-navbtn"
              onClick={() => setIdx((v) => (v + 1) % items.length)}
              aria-label="Next"
              title="Next"
            >
              ›
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function PostCourseStaffReviewPanel({
  currentUser,
  selectedPerson,
  enrollmentsForPerson,
  resolveCourseFromKey,
  POST_COURSE_MODULES_BY_KEY,
  fetchEnrollmentsForUser,
  setPersonEnrollmentsById,
}) {
  const viewerRole = String(currentUser?.role || "").toLowerCase();
  const isStaffViewer = viewerRole === "trainer" || viewerRole === "master" || viewerRole === "grandmaster";
  const personId = String(selectedPerson?._id || selectedPerson?.id || "");

  const eligible = useMemo(() => {
    const keys = (enrollmentsForPerson || []).map(getCourseKeyFromEnrollment).filter(Boolean);

    return keys
      .filter((k) => !!POST_COURSE_MODULES_BY_KEY?.[k])
      .map((courseKey) => {
        const enrollment = (enrollmentsForPerson || []).find((e) => getCourseKeyFromEnrollment(e) === courseKey) || null;
        const module = POST_COURSE_MODULES_BY_KEY?.[courseKey] || null;
        return { courseKey, enrollment, module };
      })
      .filter((x) => x.enrollment && x.module);
  }, [enrollmentsForPerson, POST_COURSE_MODULES_BY_KEY]);

  const [open, setOpen] = useState(false);
  const [openCourseKey, setOpenCourseKey] = useState("");
  const [openSectionKeyByCourse, setOpenSectionKeyByCourse] = useState({}); // { [courseKey]: sectionId }

  const [feedbackDrafts, setFeedbackDrafts] = useState({}); // { "courseKey::sectionId": "text" }
  const [feedbackFilesByKey, setFeedbackFilesByKey] = useState({}); // { "courseKey::sectionId": File[] }
  const [savingKey, setSavingKey] = useState(""); // "courseKey::sectionId::approve" etc

  // Lightbox state
  const [lbOpen, setLbOpen] = useState(false);
  const [lbItems, setLbItems] = useState([]);
  const [lbStart, setLbStart] = useState(0);

  const openLightbox = (media, startIndex) => {
    const items = (media || [])
      .filter((m) => m?.url)
      .map((m) => ({
        url: m.url,
        kind: isVideoFile(m) ? "video" : "image",
        name: m.name || "",
        fileType: m.fileType || "",
      }));

    if (!items.length) return;
    setLbItems(items);
    setLbStart(Math.max(0, Math.min(items.length - 1, Number(startIndex || 0))));
    setLbOpen(true);
  };

  const refresh = useCallback(async () => {
    if (!personId || !fetchEnrollmentsForUser || !setPersonEnrollmentsById) return;

    const res = await fetchEnrollmentsForUser(personId);
    const enrollmentsArr = Array.isArray(res)
      ? res
      : Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res?.enrollments)
      ? res.enrollments
      : [];

    setPersonEnrollmentsById((prev) => ({ ...(prev || {}), [String(personId)]: enrollmentsArr }));
  }, [personId, fetchEnrollmentsForUser, setPersonEnrollmentsById]);

  const toggleCourse = (courseKey) => setOpenCourseKey((prev) => (prev === courseKey ? "" : courseKey));

  const toggleSection = (courseKey, sectionId) => {
    const sid = String(sectionId || "");
    setOpenSectionKeyByCourse((prev) => {
      const cur = String(prev?.[courseKey] || "");
      return { ...(prev || {}), [courseKey]: cur === sid ? "" : sid };
    });
  };

  const setDraft = (courseKey, sectionId, text) => {
    const k = `${courseKey}::${sectionId}`;
    setFeedbackDrafts((prev) => ({ ...(prev || {}), [k]: text }));
  };

  const getDraft = (courseKey, sectionId) => String(feedbackDrafts?.[`${courseKey}::${sectionId}`] || "");
  const getFeedbackFiles = (courseKey, sectionId) => feedbackFilesByKey?.[`${courseKey}::${sectionId}`] || [];

  // ✅ panel should auto-open ONLY if there is a "submitted" waiting review
  const hasAnySubmittedWaiting = useMemo(() => {
    for (const { enrollment, module } of eligible) {
      const sections = Array.isArray(module?.sections) ? module.sections : [];
      for (const s of sections) {
        const sectionId = s?.id || s?.key || s?._id || s?.title;
        const rec = getPostCourseSectionRecord(enrollment, sectionId);
        if (String(rec?.status || "").toLowerCase() === "submitted") return true;
      }
    }
    return false;
  }, [eligible]);

  // auto-open/collapse on changes in eligibility
  useEffect(() => {
    // if there’s something submitted waiting review -> open
    // otherwise collapse
    setOpen(!!hasAnySubmittedWaiting);
  }, [hasAnySubmittedWaiting, personId]);

  // Auto-target first submitted section when open
  useEffect(() => {
    if (!open) return;
    if (openCourseKey && openSectionKeyByCourse?.[openCourseKey]) return;

    for (const { courseKey, enrollment, module } of eligible) {
      const sections = Array.isArray(module?.sections) ? module.sections : [];
      const target = sections.find((s) => {
        const sectionId = s?.id || s?.key || s?._id || s?.title;
        const rec = getPostCourseSectionRecord(enrollment, sectionId);
        return String(rec?.status || "").toLowerCase() === "submitted";
      });

      if (target) {
        const sectionId = target?.id || target?.key || target?._id || target?.title;
        setOpenCourseKey(courseKey);
        setOpenSectionKeyByCourse((prev) => ({ ...(prev || {}), [courseKey]: String(sectionId) }));
        break;
      }
    }
  }, [open, eligible, openCourseKey, openSectionKeyByCourse]);

  /**
   * ✅ Review:
   * Only allowed when status === "submitted"
   */
  const review = async ({ enrollmentId, courseKey, sectionId, action }) => {
    const feedback = getDraft(courseKey, sectionId).trim();
    const files = getFeedbackFiles(courseKey, sectionId);

    try {
      setSavingKey(`${courseKey}::${sectionId}::${action}`);
      await EnrollmentAPI.reviewPostCourseAssignment(enrollmentId, sectionId, {
        action, // "approve" | "request_changes"
        feedback,
        files,
      });
      setFeedbackFilesByKey((prev) => ({ ...(prev || {}), [`${courseKey}::${sectionId}`]: [] }));
      await refresh();
    } catch (e) {
      console.error(e);
      alert("Could not update this submission.");
    } finally {
      setSavingKey("");
    }
  };



  // ✅ header pill state across ALL courses/sections
  const headerState = useMemo(() => {
    let anySubmitted = false;
    let anyChanges = false;
    let anyPassed = false;
    let anyInModule = false;

    for (const { enrollment, module } of eligible) {
      const sections = Array.isArray(module?.sections) ? module.sections : [];
      for (const s of sections) {
        anyInModule = true;
        const sectionId = s?.id || s?.key || s?._id || s?.title;
        const rec = getPostCourseSectionRecord(enrollment, sectionId);
        const st = String(rec?.status || "").toLowerCase();
        if (st === "submitted") anySubmitted = true;
        if (st === "changes_requested") anyChanges = true;
        if (st === "passed") anyPassed = true;
      }
    }

    // passed if module exists and every section is passed
    let allPassed = false;
    if (anyInModule) {
      allPassed = eligible.every(({ enrollment, module }) => {
        const sections = Array.isArray(module?.sections) ? module.sections : [];
        if (!sections.length) return false;
        return sections.every((s) => {
          const sectionId = s?.id || s?.key || s?._id || s?.title;
          const rec = getPostCourseSectionRecord(enrollment, sectionId);
          return String(rec?.status || "").toLowerCase() === "passed";
        });
      });
    }

    if (anySubmitted) return { kind: "needs_review", label: "Needs review" };
    if (anyChanges) return { kind: "changes_requested", label: "Changes requested" };
    if (allPassed) return { kind: "passed", label: "✅ Passed" };
    if (anyPassed) return { kind: "in_progress", label: "In progress" };
    return { kind: "in_progress", label: "In progress" };
  }, [eligible]);

  const headerPillClass =
    headerState.kind === "passed"
      ? "dash-status-pill dash-status-approved"
      : headerState.kind === "needs_review"
      ? "dash-status-pill dash-status-submitted"
      : headerState.kind === "changes_requested"
      ? "dash-status-pill dash-status-changes"
      : "dash-status-pill";

      // ✅ SAFE early returns — AFTER all hooks
if (!isStaffViewer) return null;
if (!eligible.length) return null;

  return (
    <>
      <div className="dash-person-section dash-person-section--postcourse">
        <div
          className="dash-person-section-header dash-section-toggle"
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              setOpen((v) => !v);
            }
          }}
          aria-expanded={open}
          style={{ cursor: "pointer", userSelect: "none" }}
        >
          <span className="dash-section-toggle-left" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="dash-section-caret">{open ? "▾" : "▸"}</span>
            <span>Post-Course Submissions</span>
            <span className={headerPillClass} style={{ marginLeft: 6 }}>
              {headerState.label}
            </span>
          </span>

          <span className="dash-person-section-sub">
            Review post-course assignments. Preview images, zoom, and approve/request changes with required feedback.
          </span>
        </div>

        {open && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {eligible.map(({ courseKey, enrollment, module }) => {
              const meta = resolveCourseFromKey?.(courseKey);
              const sections = Array.isArray(module?.sections) ? module.sections : [];

              const passedCount = sections.filter((s) => {
                const sectionId = s?.id || s?.key || s?._id || s?.title;
                const rec = getPostCourseSectionRecord(enrollment, sectionId);
                return String(rec?.status || "").toLowerCase() === "passed";
              }).length;

              const pct = sections.length ? Math.round((passedCount / sections.length) * 100) : 0;
              const isCourseOpen = openCourseKey === courseKey;

              return (
                <div key={courseKey} className="dash-course-card" style={{ padding: 14 }}>
                  {/* Course header */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleCourse(courseKey)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleCourse(courseKey);
                      }
                    }}
                    style={{ display: "flex", justifyContent: "space-between", gap: 10, cursor: "pointer" }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div className="dash-course-title" style={{ fontSize: 15 }}>
                        {meta?.title || courseKey}
                      </div>
                      <div className="dash-course-meta" style={{ marginTop: 2 }}>
                        Progress:{" "}
                        <span className="dash-course-meta-strong">
                          {passedCount}/{sections.length}
                        </span>{" "}
                        sections passed
                      </div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span className="dash-status-pill" style={{ opacity: 0.85 }}>
                        {pct}%
                      </span>
                      <span style={{ opacity: 0.85 }}>{isCourseOpen ? "▾" : "▸"}</span>
                    </div>
                  </div>

                  {/* Course progress bar */}
                  <div className="dash-course-progress" style={{ marginTop: 10 }}>
                    <div
                      className="dash-course-progress-bar"
                      role="progressbar"
                      aria-valuenow={pct}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    >
                      <div className="dash-course-progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="dash-course-progress-text">{pct}% complete</span>
                  </div>

                  {/* Sections */}
                  {isCourseOpen && (
                    <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                      {sections.map((s, idx) => {
                        const sectionId = String(s?.id || s?.key || s?._id || s?.title || idx);
                        const rec = getPostCourseSectionRecord(enrollment, sectionId);
                        const status = String(rec?.status || "in_progress").toLowerCase();

                        const isSubmitted = status === "submitted";
                        const isChangesRequested = status === "changes_requested";
                        const isPassed = status === "passed";

                        // ✅ ONLY allow review when submitted
                        const canReview = isSubmitted;

                        // show submission when submitted / changes_requested / passed (or if submittedAt exists)
                        const hasSubmission =
                          !!rec?.submittedAt || isSubmitted || isChangesRequested || isPassed;

                        const isOpen = String(openSectionKeyByCourse?.[courseKey] || "") === sectionId;

                        const media = Array.isArray(rec?.media) ? rec.media : [];
                        const images = media.filter(isImageFile);
                        const videos = media.filter(isVideoFile);
                        const other = media.filter((m) => !isImageFile(m) && !isVideoFile(m));

                        // ✅ per-section pill label
                        const sectionPill =
                          isSubmitted ? { text: "Needs review", cls: "dash-status-pill dash-status-submitted" } :
                          isChangesRequested ? { text: "Changes requested", cls: "dash-status-pill dash-status-changes" } :
                          isPassed ? { text: "✅ Passed", cls: "dash-status-pill dash-status-approved" } :
                          null;

                        return (
                          <div
                            key={sectionId}
                            style={{
                              borderRadius: 16,
                              border: "1px solid rgba(255,255,255,0.10)",
                              background: "rgba(0,0,0,0.16)",
                              overflow: "hidden",
                            }}
                          >
                            {/* Section header */}
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => toggleSection(courseKey, sectionId)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  toggleSection(courseKey, sectionId);
                                }
                              }}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                gap: 10,
                                padding: "10px 12px",
                                cursor: "pointer",
                                alignItems: "center",
                                background: "rgba(255,255,255,0.03)",
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div className="dash-course-meta">
                                  <span className="dash-course-meta-strong">{s?.title || `Section ${idx + 1}`}</span>
                                </div>

                                <div className="dash-course-meta" style={{ opacity: 0.85 }}>
                                  Status:{" "}
                                  <span className="dash-course-meta-strong">{status.replaceAll("_", " ")}</span>
                                  {rec?.submittedAt ? ` • Submitted: ${formatDateTimeLocal(rec.submittedAt)}` : ""}
                                  {rec?.reviewedAt ? ` • Reviewed: ${formatDateTimeLocal(rec.reviewedAt)}` : ""}
                                </div>
                              </div>

                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {/* ✅ show submission tag if there is submission data */}
                                {hasSubmission && (
                                  <span className="dash-status-pill" style={{ opacity: 0.85 }}>
                                    Submission
                                  </span>
                                )}

                                {/* ✅ show correct state pill */}
                                {sectionPill ? (
                                  <span className={sectionPill.cls} style={{ opacity: 0.95 }}>
                                    {sectionPill.text}
                                  </span>
                                ) : null}

                                <span style={{ opacity: 0.85 }}>{isOpen ? "▾" : "▸"}</span>
                              </div>
                            </div>

                            {/* Section body */}
                            {isOpen && (
                              <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                                {!hasSubmission ? (
                                  <div className="dash-course-note">No submission yet for this section.</div>
                                ) : (
                                  <>
                                    {/* Submitted text (optional) */}
                                    {rec?.text ? (
                                      <div
                                        style={{
                                          border: "1px solid rgba(255,255,255,0.08)",
                                          borderRadius: 14,
                                          padding: 10,
                                          background: "rgba(255,255,255,0.03)",
                                        }}
                                      >
                                        <div className="dash-course-meta" style={{ marginBottom: 6 }}>
                                          <span className="dash-course-meta-strong">Student notes</span>
                                        </div>
                                        <div className="dash-course-meta" style={{ opacity: 0.92, lineHeight: 1.5 }}>
                                          {rec.text}
                                        </div>
                                      </div>
                                    ) : null}

                                    {s?.assignment?.instructions ? (
                                      <div
                                        style={{
                                          border: "1px solid rgba(255,255,255,0.08)",
                                          borderRadius: 14,
                                          padding: 10,
                                          background: "rgba(255,255,255,0.03)",
                                        }}
                                      >
                                        <div className="dash-course-meta" style={{ marginBottom: 6 }}>
                                          <span className="dash-course-meta-strong">Student prompt</span>
                                        </div>
                                        <div className="dash-course-meta" style={{ opacity: 0.92, lineHeight: 1.5 }}>
                                          {s.assignment.instructions}
                                        </div>
                                      </div>
                                    ) : null}

                                    {/* Images */}
                                    {images.length > 0 && (
                                      <div>
                                        <div className="dash-course-meta" style={{ marginBottom: 8 }}>
                                          <span className="dash-course-meta-strong">Photos</span>{" "}
                                          <span style={{ opacity: 0.75 }}>({images.length})</span>
                                          <span style={{ marginLeft: 8, opacity: 0.7 }}>
                                            Click any photo to zoom • arrows to navigate
                                          </span>
                                        </div>

                                        <div className="pc-media-grid">
                                          {images.map((m, i) => (
                                            <button
                                              key={`img-${sectionId}-${i}`}
                                              type="button"
                                              className="pc-media-thumb"
                                              onClick={() => openLightbox(media, media.findIndex((x) => x?.url === m?.url))}
                                              title="Open preview"
                                            >
                                              <img src={m.url} alt={m.name || "photo"} loading="lazy" />
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Videos */}
                                    {videos.length > 0 && (
                                      <div style={{ marginTop: 2 }}>
                                        <div className="dash-course-meta" style={{ marginBottom: 8 }}>
                                          <span className="dash-course-meta-strong">Videos</span>{" "}
                                          <span style={{ opacity: 0.75 }}>({videos.length})</span>
                                        </div>

                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                          {videos.map((m, i) => (
                                            <button
                                              key={`vid-${sectionId}-${i}`}
                                              type="button"
                                              className="dash-course-cta dash-course-cta-secondary"
                                              onClick={() => openLightbox(media, media.findIndex((x) => x?.url === m?.url))}
                                              style={{ display: "inline-flex", width: "fit-content" }}
                                              title="Open video"
                                            >
                                              {m.name || `Video ${i + 1}`} →
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* Other files */}
                                    {other.length > 0 && (
                                      <div style={{ marginTop: 2 }}>
                                        <div className="dash-course-meta" style={{ marginBottom: 8 }}>
                                          <span className="dash-course-meta-strong">Files</span>{" "}
                                          <span style={{ opacity: 0.75 }}>({other.length})</span>
                                        </div>

                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                          {other.map((m, i) => (
                                            <a
                                              key={`file-${sectionId}-${i}`}
                                              href={m.url}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="dash-course-cta dash-course-cta-secondary"
                                              style={{ display: "inline-flex", width: "fit-content" }}
                                            >
                                              {m.name || `File ${i + 1}`} →
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {/* ✅ Feedback + actions ONLY if submitted */}
                                    {canReview ? (
                                      <>
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                          <div className="dash-course-meta">
                                            <span className="dash-course-meta-strong">Feedback (optional)</span>
                                          </div>

                                          <textarea
                                            value={getDraft(courseKey, sectionId)}
                                            onChange={(e) => {
                                              setDraft(courseKey, sectionId, e.target.value);
                                              autoGrowTextarea(e.target);
                                            }}
                                            onInput={(e) => autoGrowTextarea(e.target)}
                                            rows={3}
                                            className="pc-feedback-textarea"
                                            placeholder="Write clear feedback for the student…"
                                          />

                                          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                                            <div className="dash-course-meta">
                                              <span className="dash-course-meta-strong">Feedback photos (optional)</span>
                                            </div>
                                            <input
                                              type="file"
                                              accept="image/*"
                                              multiple
                                              className="pc-feedback-file-input"
                                              onChange={(e) => {
                                                const selected = Array.from(e.target.files || []);
                                                setFeedbackFilesByKey((prev) => ({
                                                  ...(prev || {}),
                                                  [`${courseKey}::${sectionId}`]: selected,
                                                }));
                                              }}
                                            />
                                            {getFeedbackFiles(courseKey, sectionId).length > 0 ? (
                                              <div className="pc-feedback-file-list">
                                                {getFeedbackFiles(courseKey, sectionId).map((file) => (
                                                  <span key={`${file.name}-${file.size}`} className="pc-feedback-file-chip">
                                                    {file.name}
                                                  </span>
                                                ))}
                                              </div>
                                            ) : null}
                                          </div>

                                          {rec?.feedback && (
                                            <div className="dash-course-note" style={{ marginTop: 4 }}>
                                              Last feedback: <span style={{ opacity: 0.9 }}>{rec.feedback}</span>
                                            </div>
                                          )}
                                          {Array.isArray(rec?.reviewMedia) && rec.reviewMedia.length > 0 ? (
                                            <div>
                                              <div className="dash-course-meta" style={{ marginBottom: 8 }}>
                                                <span className="dash-course-meta-strong">Last feedback photos</span>
                                              </div>
                                              <div className="pc-media-grid">
                                                {rec.reviewMedia.map((m, i) => (
                                                  <button
                                                    key={`feedback-img-${sectionId}-${i}`}
                                                    type="button"
                                                    className="pc-media-thumb"
                                                    onClick={() =>
                                                      openLightbox(
                                                        rec.reviewMedia,
                                                        rec.reviewMedia.findIndex((x) => x?.url === m?.url)
                                                      )
                                                    }
                                                  >
                                                    <img src={m.url} alt={m.name || "feedback"} loading="lazy" />
                                                  </button>
                                                ))}
                                              </div>
                                            </div>
                                          ) : null}
                                        </div>

                                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                                          {!getDraft(courseKey, sectionId).trim() && (
                                            <button
                                              type="button"
                                              className="dash-chip-button"
                                              onClick={() =>
                                                review({
                                                  enrollmentId: enrollment?._id,
                                                  courseKey,
                                                  sectionId,
                                                  action: "approve",
                                                })
                                              }
                                              disabled={savingKey === `${courseKey}::${sectionId}::approve`}
                                              style={{ opacity: savingKey === `${courseKey}::${sectionId}::approve` ? 0.65 : 1 }}
                                            >
                                              {savingKey === `${courseKey}::${sectionId}::approve` ? "Saving…" : "Approve"}
                                            </button>
                                          )}

                                          <button
                                            type="button"
                                            className="dash-chip-button dash-chip-button-secondary"
                                            onClick={() =>
                                              review({
                                                enrollmentId: enrollment?._id,
                                                courseKey,
                                                sectionId,
                                                action: "request_changes",
                                              })
                                            }
                                            disabled={savingKey === `${courseKey}::${sectionId}::request_changes`}
                                            style={{
                                              opacity: savingKey === `${courseKey}::${sectionId}::request_changes` ? 0.65 : 1,
                                            }}
                                          >
                                            {savingKey === `${courseKey}::${sectionId}::request_changes`
                                              ? "Saving…"
                                              : "Request changes"}
                                          </button>
                                        </div>
                                      </>
                                    ) : (
                                      // ✅ changes_requested or passed: read-only summary (no textarea/buttons)
                                      <div className="dash-course-note">
                                        {isPassed ? "✅ Passed" : isChangesRequested ? "Changes requested" : "—"}
                                        {rec?.reviewedAt ? ` • ${formatDateTimeLocal(rec.reviewedAt)}` : ""}
                                        {rec?.feedback ? (
                                          <div style={{ marginTop: 6 }}>
                                            <strong>Feedback:</strong> {rec.feedback}
                                          </div>
                                        ) : null}
                                        {Array.isArray(rec?.reviewMedia) && rec.reviewMedia.length > 0 ? (
                                          <div style={{ marginTop: 10 }}>
                                            <div className="dash-course-meta" style={{ marginBottom: 8 }}>
                                              <span className="dash-course-meta-strong">Feedback photos</span>
                                            </div>
                                            <div className="pc-media-grid">
                                              {rec.reviewMedia.map((m, i) => (
                                                <button
                                                  key={`feedback-readonly-${sectionId}-${i}`}
                                                  type="button"
                                                  className="pc-media-thumb"
                                                  onClick={() =>
                                                    openLightbox(
                                                      rec.reviewMedia,
                                                      rec.reviewMedia.findIndex((x) => x?.url === m?.url)
                                                    )
                                                  }
                                                >
                                                  <img src={m.url} alt={m.name || "feedback"} loading="lazy" />
                                                </button>
                                              ))}
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Lightbox open={lbOpen} items={lbItems} startIndex={lbStart} onClose={() => setLbOpen(false)} />
    </>
  );
}
